import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiLogOut,
  FiPieChart,
  FiPackage,
  FiHome,
  FiSun,
  FiMoon,
  FiMenu,
  FiX,
  FiShoppingCart,
  FiShoppingBag,
  FiUsers,
} from "react-icons/fi";

import axios from "axios";
import { FiBook } from "react-icons/fi";

import UserManagement from "./UserManagement";
import AnalyticsDashboard from "./AnalyticsDashboard";

function AdminDashboard() {
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics'); 
  const navigate = useNavigate();

  // Check for saved theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setDarkMode(true);
    }
  }, []);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // Token validation
  useEffect(() => {
    const validateToken = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/admin-login");
          return;
        }
        const response = await axios.get(
          "https://firstaidba.vercel.app/api/admin/validate",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.valid) {
          setIsValid(true);
        } else {
          navigate("/admin-login");
        }
      } catch (err) {
        console.error("Token validation error:", err);
        navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/admin-login");
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-purple-500 dark:border-purple-400 border-t-transparent rounded-full"
        ></motion.div>
      </div>
    );
  }

  if (!isValid) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex">
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMobileMenu}
        className="md:hidden fixed top-4 left-4 z-50 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg"
      >
        {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </button>

      {/* Sidebar */}
      <motion.div
        initial={{ x: -300, opacity: 0 }}
        animate={{
          x: mobileMenuOpen ? 0 : window.innerWidth < 768 ? -300 : 0,
          opacity: mobileMenuOpen ? 1 : window.innerWidth < 768 ? 0 : 1,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="w-72 bg-gradient-to-b from-purple-900 to-purple-800 dark:from-gray-800 dark:to-gray-900 text-white p-4 sm:p-6 fixed h-full shadow-xl z-40 md:block"
      >
        <h2 className="text-xl sm:text-2xl font-bold mb-8 flex items-center gap-2">
          <span className="bg-white text-purple-800 dark:bg-gray-700 dark:text-purple-300 p-2 rounded-lg">
            <FiPieChart size={20} />
          </span>
          <span>Firstaid-pro Admin</span>
        </h2>
        <nav>
          <ul className="space-y-2">
            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => handleNavigation("/admin-dashboard")}
                className="w-full text-left py-3 px-4 rounded-lg hover:bg-purple-700 dark:hover:bg-gray-700 flex items-center gap-3 text-sm sm:text-base"
              >
                <FiHome size={20} />
                Dashboard
              </motion.button>
            </li>
            

            <li>
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => handleNavigation("/user-management")}
                className="w-full text-left py-3 px-4 rounded-lg hover:bg-purple-700 dark:hover:bg-gray-700 flex items-center gap-3 text-sm sm:text-base"
              >
                <FiUsers size={20} />
                User Management
              </motion.button>
            </li>

            <li className="mt-8">
              <motion.button
                whileHover={{ x: 5 }}
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left py-3 px-4 rounded-lg hover:bg-red-700 flex items-center gap-3 text-sm sm:text-base"
              >
                <FiLogOut size={20} />
                Logout
              </motion.button>
            </li>
          </ul>
        </nav>
      </motion.div>

      {/* Main Content */}


      {/* <div className="md:ml-72 p-4 sm:p-6 flex-1">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 gap-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent"
          >
            Firstaid-pro Management Dashboard
          </motion.h1>
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 py-2 px-4 rounded-lg shadow-md hover:shadow-lg flex items-center gap-2 text-sm sm:text-base"
            >
              {darkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
              {darkMode ? "Light Mode" : "Dark Mode"}
            </motion.button>
          </div>
        </div>

        <UserManagement />
      </div>
    </div>
  );
}
 */}
  {/* Main Content */}
      <div className="md:ml-72 p-4 sm:p-6 flex-1">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 gap-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 bg-clip-text text-transparent"
          >
            Firstaid-pro Management Dashboard
          </motion.h1>
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              className="bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 py-2 px-4 rounded-lg shadow-md hover:shadow-lg flex items-center gap-2 text-sm sm:text-base"
            >
              {darkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
              {darkMode ? "Light Mode" : "Dark Mode"}
            </motion.button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'analytics'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'users'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            User Management
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'analytics' && <AnalyticsDashboard />}
        {activeTab === 'users' && <UserManagement />}
      </div>
    </div>
  );
}


export default AdminDashboard;
